import { keepPreviousData, queryOptions, useQuery } from "@tanstack/react-query"
import { GET } from "@/api/client"
import type {
	RegistryAccountResponse,
	RegistryAccountsParams,
} from "@/api/types"

export const registryAccountsPrefixKey = ["registries", "accounts"] as const

export function useRegistryAccount(
	accountId: number | null,
	options: { placeholderData?: RegistryAccountResponse } = {},
) {
	return useQuery({
		queryKey: ["registries", "accounts", "detail", accountId] as const,
		queryFn: () =>
			GET("/api/v1/registries/accounts/{account_id}", {
				params: { path: { account_id: accountId as number } },
			}),
		enabled: accountId != null && accountId > 0,
		placeholderData: options.placeholderData,
	})
}

export function registryAccountsListOptions(
	params: RegistryAccountsParams = {},
) {
	return queryOptions({
		queryKey: ["registries", "accounts", "list", params] as const,
		queryFn: () =>
			GET("/api/v1/registries/accounts", {
				params: { query: params },
			}),
		placeholderData: keepPreviousData,
	})
}

export function useRegistryAccountsList(params: RegistryAccountsParams = {}) {
	return useQuery(registryAccountsListOptions(params))
}

export function registryAccountStatsOptions(companyId?: number) {
	return queryOptions({
		queryKey: ["registries", "accounts", "stats", companyId ?? null] as const,
		queryFn: () =>
			GET("/api/v1/registries/accounts/stats", {
				params: { query: companyId != null ? { companyId } : {} },
			}),
	})
}

export function useRegistryAccountStats(companyId?: number) {
	return useQuery(registryAccountStatsOptions(companyId))
}

export function registryAccountCountriesOptions(companyId?: number) {
	return queryOptions({
		queryKey: [
			"registries",
			"accounts",
			"countries",
			companyId ?? null,
		] as const,
		queryFn: () =>
			GET("/api/v1/registries/accounts/countries", {
				params: { query: companyId != null ? { companyId } : {} },
			}),
	})
}

export function useRegistryAccountCountries(opts?: {
	companyId?: number
	enabled?: boolean
}) {
	return useQuery({
		...registryAccountCountriesOptions(opts?.companyId),
		enabled: opts?.enabled,
	})
}
